import styles from "./TaskModal.module.scss";
import { CloseIcon } from "../../icons/CloseIcon";
import { AddIcon } from "../../icons/AddIcon";
import { useState, useRef, useEffect } from "react";
import { ArrowSubmit } from "../../icons/ArrowSubmit";
import { submitClientApplication } from "../../services/client-application.service";
import { useForm } from "react-hook-form";
import type { SubmitHandler } from "react-hook-form";
import { TaskType } from "../../types/task-type";
import { useTranslation } from "../../hooks/useTranslation";
import successVideo from "/success.mp4";
import { Accordion } from "./Accordion";
import type { TechnicalTaskResponse } from "../../types/technical-task";
import { useTelegram } from "../../hooks/useTelegram";

interface TaskModalProps {
  onClose: () => void;
  taskData: TechnicalTaskResponse | null;
  hasTasks: boolean;
}

interface FormData {
  name: string;
  phone: string;
  taskType?: TaskType;
  details: string;
}

interface ServerError {
  [key: string]: string[];
}

export const TaskModal = ({ onClose, taskData, hasTasks }: TaskModalProps) => {
  const { t, currentLang } = useTranslation();
  const { user } = useTelegram();
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [serverErrors, setServerErrors] = useState<ServerError | null>(null);
  const [isSelectOpen, setIsSelectOpen] = useState(false);
  const [selectedTaskType, setSelectedTaskType] = useState<TaskType | "">("");
  const selectRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (selectRef.current && !selectRef.current.contains(event.target as Node)) {
        setIsSelectOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const {
    register,
    handleSubmit: handleFormSubmit,
    formState: { errors },
  } = useForm<FormData>({
    defaultValues: {
      name: "",
      phone: "",
      taskType: undefined,
      details: "",
    },
  });

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      onClose();
    }, 300);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) {
      const currentFilesCount = selectedFiles.length;
      const availableSlots = 3 - currentFilesCount;

      if (availableSlots <= 0) {
        return;
      }

      const filesToAdd = files.slice(0, availableSlots);
      const newFiles = [...selectedFiles, ...filesToAdd];
      setSelectedFiles(newFiles);
    }
  };

  const removeFile = (index: number) => {
    const newFiles = selectedFiles.filter((_, i) => i !== index);
    setSelectedFiles(newFiles);
    const fileInput = document.getElementById("fileInput") as HTMLInputElement;
    if (fileInput) {
      fileInput.value = "";
    }
  };

  const formatFileSize = (bytes: number): string => {
    const kb = Math.round(bytes / 1024);
    return `${kb} ${t("fileSize")}`;
  };

  const formatFileDate = (file: File): string => {
    const date = new Date(file.lastModified);

    const options: Intl.DateTimeFormatOptions = {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    };

    let locale = "en-US";
    if (currentLang === "ru") {
      locale = "ru-RU";
    } else if (currentLang === "uk") {
      locale = "uk-UA";
    }

    const formattedDate = new Intl.DateTimeFormat(locale, options).format(date);

    if (currentLang === "ru" || currentLang === "uk") {
      return formattedDate.replace(/(\d{2})\.(\d{2})\.(\d{4}), (\d{2}):(\d{2})/, "$1.$2.$3, $4:$5");
    }

    return formattedDate.replace(/(\d{2})\/(\d{2})\/(\d{4}), (\d{2}):(\d{2})/, "$1.$2.$3, $4:$5");
  };

  const taskTypeOptions = [
    { value: TaskType.Website, label: t("taskTypes.website") },
    { value: TaskType.TelegramBot, label: t("taskTypes.telegramBot") },
    { value: TaskType.ProductSupport, label: t("taskTypes.productSupport") },
    { value: TaskType.MobileApp, label: t("taskTypes.mobileApp") },
    { value: TaskType.CRMSystem, label: t("taskTypes.crmSystem") },
    { value: TaskType.Security, label: t("taskTypes.security") },
    { value: TaskType.APIIntegration, label: t("taskTypes.apiIntegration") },
    { value: TaskType.Another, label: t("taskTypes.another") },
  ];

  const handleTaskTypeSelect = (value: TaskType) => {
    setSelectedTaskType(value);
    setIsSelectOpen(false);
  };

  const onSubmit: SubmitHandler<FormData> = async (data) => {
    setIsSubmitting(true);
    setServerErrors(null);

    try {
      const applicationData = {
        name: data.name,
        email: "",
        telegram_username: user?.username || "",
        phone: data.phone,
        type: selectedTaskType || TaskType.Website,
        description: data.details,
        telegram_user_id: user?.id?.toString(),
        files: selectedFiles.length > 0 ? selectedFiles : undefined,
      };

      await submitClientApplication(applicationData);
      setIsSuccess(true);
    } catch (err: unknown) {
      console.error("Error submitting client application:", err);

      if (
        err &&
        typeof err === "object" &&
        "response" in err &&
        err.response &&
        typeof err.response === "object" &&
        "data" in err.response &&
        err.response.data
      ) {
        setServerErrors(err.response.data as ServerError);
      } else {
        setServerErrors({
          general: [err instanceof Error ? err.message : "Произошла ошибка при отправке формы"],
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={`${styles.modal} ${isClosing ? styles.closing : ""}`}>
      <div className={`${styles.container} ${isClosing ? styles.closing : ""}`}>
        {isSuccess ? (
          <div className={styles.successContainer}>
            <div className={styles.successContent}>
              <h2 className={styles.title} dangerouslySetInnerHTML={{ __html: t("modal.success.title") }} />
              <p className={styles.description}>{t("modal.success.message")}</p>
            </div>
            <div className={styles.successVideo}>
              <video width={375} height={452} src={successVideo} autoPlay loop muted playsInline />
            </div>
            <div className={styles.successButton}>
              <button type="button" className={styles.submitButton} onClick={handleClose}>
                {t("modal.closeButton")}
              </button>
            </div>
          </div>
        ) : (
          <div className={styles.content}>
            <header className={styles.header}>
              <h2 className={styles.title} dangerouslySetInnerHTML={{ __html: t("modal.title") }} />
              <p className={styles.description}>{t("modal.description")}</p>
            </header>
            <form className={styles.form} onSubmit={handleFormSubmit(onSubmit)}>
              <div className={styles.contentBody}>
                <div className={styles.formGroup}>
                  <div className={`${styles.inputWrapper} ${errors.name || serverErrors?.name ? styles.error : ""}`}>
                    <input
                      type="text"
                      placeholder={t("modal.namePlaceholder")}
                      {...register("name", { required: t("validation.nameRequired") })}
                    />
                    {(errors.name || serverErrors?.name) && (
                      <div className={styles.fieldError}>{serverErrors?.name?.[0] || errors.name?.message}</div>
                    )}
                  </div>
                  <div className={`${styles.inputWrapper} ${errors.phone || serverErrors?.phone ? styles.error : ""}`}>
                    <input
                      type="tel"
                      placeholder={t("modal.phonePlaceholder")}
                      {...register("phone", {
                        required: t("validation.phoneRequired"),
                        minLength: {
                          value: 9,
                          message: t("validation.phoneMinLength"),
                        },
                      })}
                    />
                    {(errors.phone || serverErrors?.phone) && (
                      <div className={styles.fieldError}>{serverErrors?.phone?.[0] || errors.phone?.message}</div>
                    )}
                  </div>
                  <div
                    className={`${styles.inputWrapper} ${errors.taskType || serverErrors?.type ? styles.error : ""}`}
                  >
                    <div className={`${styles.customSelect} ${isSelectOpen ? styles.open : ""}`} ref={selectRef}>
                      <div className={styles.selectTrigger} onClick={() => setIsSelectOpen(!isSelectOpen)}>
                        <span className={selectedTaskType ? styles.selectedValue : styles.placeholder}>
                          {selectedTaskType
                            ? taskTypeOptions.find((option) => option.value === selectedTaskType)?.label
                            : t("modal.taskTypePlaceholder")}
                        </span>
                        <div className={`${styles.selectArrow} ${isSelectOpen ? styles.rotated : ""}`}>
                          <svg
                            width="20"
                            height="20"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <polyline points="6,9 12,15 18,9"></polyline>
                          </svg>
                        </div>
                      </div>
                      {isSelectOpen && (
                        <div className={styles.selectDropdown}>
                          {taskTypeOptions.map((option) => (
                            <div
                              key={option.value}
                              className={`${styles.selectOption} ${
                                selectedTaskType === option.value ? styles.selected : ""
                              }`}
                              onClick={() => handleTaskTypeSelect(option.value as TaskType)}
                            >
                              {option.label}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    <span className={styles.notSure}>{t("modal.taskTypeNotSure")}</span>
                    {serverErrors?.type && <div className={styles.fieldError}>{serverErrors.type[0]}</div>}
                  </div>

                  <div
                    className={`${styles.inputWrapper} ${
                      errors.details || serverErrors?.description ? styles.error : ""
                    }`}
                  >
                    <textarea placeholder={t("modal.detailsPlaceholder")} {...register("details")} />
                    {serverErrors?.description && (
                      <div className={styles.fieldError}>{serverErrors.description[0]}</div>
                    )}
                  </div>
                  <div className={styles.fileInputWrapper}>
                    <h3 className={styles.fileInputTitle}>{t("modal.fileUpload.title")}</h3>
                    <p className={styles.notSure}>{t("modal.fileUpload.description")}</p>
                    {selectedFiles.length > 0 && (
                      <div className={styles.files}>
                        {selectedFiles.map((file, index) => (
                          <div key={index} className={styles.fileInfo}>
                            <div className={styles.fileDetails}>
                              <div className={styles.fileType}>{file.type.slice(0, 3)}</div>
                              <div>
                                <div className={styles.fileName}>{file.name}</div>
                                <div className={styles.fileSize}>
                                  {formatFileDate(file)} • {formatFileSize(file.size)}
                                </div>
                              </div>
                            </div>
                            <button
                              type="button"
                              className={styles.removeFile}
                              onClick={() => removeFile(index)}
                              aria-label="Remove file"
                            >
                              <CloseIcon />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                    <div
                      className={`${styles.fileInput} ${serverErrors?.files ? styles.error : ""} ${
                        selectedFiles.length >= 3 ? styles.disabled : ""
                      }`}
                    >
                      <input
                        type="file"
                        id="fileInput"
                        onChange={handleFileChange}
                        accept=".pdf,.doc,.docx,.txt"
                        disabled={selectedFiles.length >= 3}
                        multiple
                      />
                      <AddIcon />
                      <span>
                        {selectedFiles.length >= 3
                          ? t("modal.fileUpload.maxFiles")
                          : t("modal.fileUpload.uploadButton")}
                      </span>
                    </div>

                    {serverErrors?.files && <div className={styles.fieldError}>{serverErrors.files[0]}</div>}
                  </div>
                  {hasTasks && taskData && (
                    <Accordion title={t("modal.briefExample.title")}>
                      <div className={styles.briefContent}>
                        <div className={styles.taskInfo}>
                          <div className={styles.taskName}>Name: {taskData.name}</div>
                          <div className={styles.taskType}>Type: {taskData.type}</div>
                        </div>
                        <div className={styles.taskText}>{taskData.text}</div>
                      </div>
                    </Accordion>
                  )}
                </div>
                {serverErrors?.general && <div className={styles.errorMessage}>{serverErrors.general[0]}</div>}
              </div>
              <div className={styles.submitContainer}>
                <button type="submit" className={styles.submitButton} disabled={isSubmitting}>
                  {isSubmitting ? t("modal.submitting") : t("modal.submitButton")}
                  <ArrowSubmit />
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};
